import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { guardarConclusiones } from '../../../actions/adminAction';

const DOMINI = process.env.REACT_APP_DOMINI_API_CIUDAD;

/**
 * ConclusionesAdmin
 * Props:
 *  - evaluacion   { IdEvaluacion, Nombre_Mun, fecha, ResultatGlobal,
 *                   conclusionesTexto, PdfUrl }
 *                  PdfUrl: URL del PDF ya generado (null si no existe todavía)
 *  - tokenJwt     string
 *  - onCerrar     fn()
 *  - onGuardado   fn(idEvaluacion, texto)
 *  - onPdfGenerado fn(idEvaluacion, pdfUrl)  — para actualizar la lista
 */
export const ConclusionesAdmin = ({
    evaluacion,
    tokenJwt,
    onCerrar,
    onGuardado,
    onPdfGenerado,
}) => {
    const dispatch = useDispatch();

    const [texto,      setTexto]      = useState(evaluacion.conclusionesTexto || '');
    const [guardando,  setGuardando]  = useState(false);
    const [msgOk,      setMsgOk]      = useState(null);
    const [msgError,   setMsgError]   = useState(null);

    // URL del PDF: puede venir ya de la evaluación o actualizarse tras generar
    const [pdfUrl,     setPdfUrl]     = useState(evaluacion.PdfUrl || null);

    const [generando,  setGenerando]  = useState(false);
    const [pdfError,   setPdfError]   = useState(null);
    const [pdfOkMsg,   setPdfOkMsg]   = useState(null);

    // ── Guardar conclusiones ──────────────────────────────────────────────────
    const handleGuardar = () => {
        if (!texto.trim()) {
            setMsgError('El texto de conclusiones no puede estar vacío.');
            return;
        }
        setMsgOk(null); setMsgError(null); setGuardando(true);
        dispatch(guardarConclusiones(
            tokenJwt, evaluacion.IdEvaluacion, texto,
            (id, textoGuardado) => {
                setMsgOk('Conclusiones guardadas correctamente.');
                setGuardando(false);
                if (onGuardado) onGuardado(id, textoGuardado);
            },
            (err) => { setMsgError(err); setGuardando(false); }
        ));
    };

    // ── Llamada al endpoint de generación ────────────────────────────────────
    // enviarEmail: true  → POST /pdf/generar  con EnviarEmail=1  (genera + envía correo)
    // enviarEmail: false → POST /pdf/generar  con EnviarEmail=0  (solo genera, sin correo)
    const llamarGenerarPdf = async (enviarEmail) => {
        setGenerando(true); setPdfError(null); setPdfOkMsg(null);
        try {
            const res = await fetch(DOMINI + '/pdf/generar', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                    'Accept': 'application/json',
                    'Authorization': tokenJwt,
                },
                credentials: 'include',
                mode: 'cors',
                body: [
                    'IdEvaluacion=' + encodeURIComponent(evaluacion.IdEvaluacion),
                    'EnviarEmail='  + encodeURIComponent(enviarEmail ? '1' : '0'),
                ].join('&'),
            });
            const data = await res.json();
            if (data.ok) {
                const url = data.contingut?.url || null;
                setPdfUrl(url);
                setPdfOkMsg(
                    enviarEmail
                        ? `✅ PDF generado y enviado a ${data.contingut?.email || 'el responsable'}.`
                        : '✅ PDF generado correctamente.'
                );
                if (onPdfGenerado && url) onPdfGenerado(evaluacion.IdEvaluacion, url);
            } else {
                setPdfError(data.msg || 'Error al generar el PDF.');
            }
        } catch {
            setPdfError('Error de conexión. Inténtalo de nuevo.');
        } finally {
            setGenerando(false);
        }
    };

    const tienePdf   = !!pdfUrl;
    const tieneConcl = texto.trim().length > 0;
    const numPal     = tieneConcl ? texto.trim().split(/\s+/).length : 0;

    return (
        <>
            {/* Overlay */}
            <div className="ca-drawer-overlay" onClick={onCerrar} />

            <div className="ca-drawer">

                {/* ── Cabecera ─────────────────────────────────────────────── */}
                <div className="ca-drawer-header">
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                        <div>
                            <p style={{ fontSize:11, textTransform:'uppercase', letterSpacing:'1px', opacity:0.75, marginBottom:4, color:'#fff' }}>
                                Conclusiones Finales
                            </p>
                            <h2 style={{ fontSize:20, fontWeight:700, margin:0, color:'#fff' }}>
                                {evaluacion.Nombre_Mun}
                            </h2>
                            <p style={{ fontSize:13, opacity:0.8, marginTop:4, color:'#fff' }}>
                                {evaluacion.fecha} &nbsp;|&nbsp; Resultado global: <strong>{evaluacion.ResultatGlobal}</strong>
                            </p>
                        </div>
                        <button onClick={onCerrar} className="res-modal-close-btn" aria-label="Cerrar">×</button>
                    </div>
                </div>

                {/* ── Cuerpo scrollable ─────────────────────────────────────── */}
                <div className="ca-drawer-body">

                    {/* Badge estado conclusiones */}
                    <div style={{
                        display:'flex', alignItems:'center', gap:8, marginBottom:20,
                        padding:'10px 14px', borderRadius:8,
                        backgroundColor: tieneConcl ? '#d1fae5' : '#fef3c7',
                        border: `1px solid ${tieneConcl ? '#6ee7b7' : '#fcd34d'}`,
                        color: tieneConcl ? '#065f46' : '#92400e',
                        fontSize:13, fontWeight:600,
                    }}>
                        <span style={{ fontSize:16 }}>{tieneConcl ? '✅' : '⏳'}</span>
                        <span>
                            {tieneConcl
                                ? 'Este informe ya tiene conclusiones redactadas.'
                                : 'Aún no se han redactado las conclusiones.'}
                        </span>
                    </div>

                    {/* Textarea conclusiones */}
                    <label className="u-field-label">Texto de conclusiones</label>
                    <textarea
                        value={texto}
                        onChange={e => { setTexto(e.target.value); setMsgOk(null); setMsgError(null); }}
                        placeholder="Redacta aquí las conclusiones finales de esta evaluación..."
                        rows={10}
                        className="ca-textarea"
                    />
                    <p style={{ fontSize:11, color:'#9ca3af', textAlign:'right', marginTop:4 }}>
                        {numPal} palabras &nbsp;·&nbsp; {texto.length} caracteres
                    </p>

                    {msgOk    && <div className="ms-alert-success" style={{ marginTop:12 }}>✓ {msgOk}</div>}
                    {msgError && <div className="ms-alert-error"   style={{ marginTop:12 }}>✗ {msgError}</div>}

                    <div className="fa-alert-info" style={{ marginTop:16, marginBottom:8 }}>
                        <strong>ℹ️</strong> Las conclusiones se incluirán en el informe PDF.
                    </div>

                    {/* ── Sección PDF ───────────────────────────────────────── */}
                    <div style={{ borderTop:'1.5px solid #f3f4f6', paddingTop:20, marginTop:16, display:'flex', flexDirection:'column', gap:10 }}>

                        <p style={{ fontSize:13, fontWeight:700, color:'#111827', margin:0 }}>
                            📄 Informe PDF
                        </p>

                        {/* Feedback generación */}
                        {pdfError && <div className="ms-alert-error">{pdfError}</div>}
                        {pdfOkMsg && <div className="ms-alert-success">{pdfOkMsg}</div>}

                        {tienePdf ? (
                            /* ── PDF ya existe ────────────────────────────── */
                            <>
                                <div style={{
                                    display:'flex', alignItems:'center', gap:8,
                                    padding:'10px 14px', borderRadius:8,
                                    backgroundColor:'#eff6ff', border:'1px solid #bfdbfe',
                                    color:'#1e40af', fontSize:13,
                                }}>
                                    <span>📎</span>
                                    <span>Ya existe un PDF generado para esta evaluación.</span>
                                </div>

                                {/* Botón Ver PDF */}
                                <a
                                    href={pdfUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="u-btn-primary"
                                    style={{
                                        display:'block', textAlign:'center',
                                        textDecoration:'none', backgroundColor:'#1d4ed8',
                                    }}
                                >
                                    👁️ Ver / Descargar PDF
                                </a>

                                <p style={{ fontSize:11, color:'#9ca3af', margin:0, textAlign:'center' }}>
                                    ¿Quieres regenerar el informe con los cambios actuales?
                                </p>

                                {/* Botones regenerar */}
                                <div style={{ display:'flex', gap:8 }}>
                                    <button
                                        onClick={() => llamarGenerarPdf(false)}
                                        disabled={generando}
                                        className="u-btn-secondary"
                                        style={{ flex:1, cursor: generando ? 'not-allowed' : 'pointer' }}
                                    >
                                        {generando ? '⏳ Generando…' : '🔄 Regenerar PDF'}
                                    </button>
                                    <button
                                        onClick={() => llamarGenerarPdf(true)}
                                        disabled={generando}
                                        className="u-btn-primary"
                                        style={{
                                            flex:1,
                                            backgroundColor: generando ? '#9ca3af' : '#111827',
                                            cursor: generando ? 'not-allowed' : 'pointer',
                                        }}
                                    >
                                        {generando ? '⏳ Enviando…' : '📧 Regenerar y enviar'}
                                    </button>
                                </div>
                            </>
                        ) : (
                            /* ── PDF no existe: dos botones ──────────────── */
                            <>
                                <p style={{ fontSize:12, color:'#6b7280', margin:0, lineHeight:1.55 }}>
                                    Genera el informe completo con todas las categorías y conclusiones.
                                </p>

                                {/* Solo genera, sin correo */}
                                <button
                                    onClick={() => llamarGenerarPdf(false)}
                                    disabled={generando}
                                    className="u-btn-secondary"
                                    style={{ width:'100%', cursor: generando ? 'not-allowed' : 'pointer' }}
                                >
                                    {generando ? '⏳ Generando…' : '📄 Generar PDF (solo admin)'}
                                </button>

                                {/* Genera y envía correo */}
                                <button
                                    onClick={() => llamarGenerarPdf(true)}
                                    disabled={generando}
                                    className="u-btn-primary"
                                    style={{
                                        width:'100%',
                                        backgroundColor: generando ? '#9ca3af' : '#111827',
                                        cursor: generando ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    {generando ? '⏳ Generando…' : '📧 Generar PDF y enviar por email'}
                                </button>
                            </>
                        )}
                    </div>

                </div>

                {/* ── Footer ───────────────────────────────────────────────── */}
                <div className="ca-drawer-footer">
                    <button className="u-btn-secondary" onClick={onCerrar}>
                        Cancelar
                    </button>
                    <button
                        className="u-btn-primary"
                        onClick={handleGuardar}
                        disabled={guardando}
                        style={{ backgroundColor: guardando ? '#9ca3af' : undefined }}
                    >
                        {guardando ? 'Guardando...' : '💾 Guardar conclusiones'}
                    </button>
                </div>

            </div>
        </>
    );
};